import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import { gql, useQuery, useSubscription } from '@apollo/client';
import LoadingIndicator from '@/components/core/LoadingIndicator';
import { makeStyles } from '@material-ui/core/styles';
import { formatTimeOfDay } from '@/components/pages/kiosk/monitor/StaffMonitor/components/MemberTableRow';
import { isBefore, startOfDay } from 'date-fns';
import { translateToTz } from '@/util/dateTime';
import Dropdown from '@/components/core/Dropdown';
import { Calendar } from 'react-date-range';
import { withRouter } from 'react-router';
import queryString from 'query-string';
import { useScope } from '@/hooks/useScope';
import { useSettings } from '@/components/pages/kiosk/monitor/StaffMonitor/functions';
import { ButtonDateSwitch, TableSection, TableSettings } from '@/components/pages/kiosk/monitor/StaffMonitor/index';
import { columns as allColumns } from '@/components/pages/kiosk/monitor/StaffMonitor/constants';
import StaffAnalytics from '@/components/pages/kiosk/monitor/StaffMonitor/components/StaffAnalytics';
import { useWebSocketReconnect } from '@/hooks/useWebSocketReconnect';

interface StaffMonitorProps {
  tableOnly?: boolean;
  hideCost?: boolean;
  disableRowClick?: boolean;
  history?: any;
  location?: any;
}

export interface TrackingSubject {
  id: string;
  role: string;
  title?: string;
  entityId: number;
  physician?: {
    id: number;
    name: string;
  };
  staffMember?: {
    id: string;
    name: string;
    compensation: string | null;
    hourlyRate: number | null;
  };
  anesthesiologist?: {
    id: number;
    name: string;
    hourlyRate?: number | null;
  };
  currentLocation: {
    room: {
      id: number;
      name: string;
      type: string;
    };
    arrivedAt: number;
  };
  arrivedOnSite: number;
  arrivedOnSiteOverride: number | null;
  leftSite: number | null;
  leftSiteOverride: number | null;
}

interface StaffMonitoring {
  title: string;
  progress: number;
  timeOfDay: number;
  subjects: TrackingSubject[];
}

function createLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const StaffMonitor = withRouter(
  ({ history, location, tableOnly = false, hideCost = false, disableRowClick = false }: StaffMonitorProps) => {
    const classes = useStyles();
    const [view, setView] = useState<'table' | 'analytics'>('table');

    // Filter columns based on hideCost prop
    const columns = useMemo(() => {
      if (hideCost) {
        return allColumns.filter(col => col.key !== 'cost');
      }
      return allColumns;
    }, [hideCost]);

    // When tableOnly is true, also disable row click
    const effectiveDisableRowClick = tableOnly || disableRowClick;

    // Track WebSocket reconnections for logging and debugging
    const reconnectCount = useWebSocketReconnect();
    const prevReconnectCount = useRef(reconnectCount);
    const [isReconnecting, setIsReconnecting] = useState(false);

    // Cache last valid subscription data to prevent flash of empty state during reconnection
    const cachedDataRef = useRef<StaffMonitoring | null>(null);

    // Log reconnection events and track reconnection state
    useEffect(() => {
      if (reconnectCount > 0 && reconnectCount !== prevReconnectCount.current) {
        console.log(
          `[StaffMonitor] WebSocket reconnected (count: ${reconnectCount}), subscription will auto-resubscribe`
        );
        prevReconnectCount.current = reconnectCount;
        setIsReconnecting(true);

        // Clear reconnecting state after data should have arrived (safety timeout)
        const timeout = setTimeout(() => {
          setIsReconnecting(false);
        }, 5000);

        return () => clearTimeout(timeout);
      }
    }, [reconnectCount]);

    const scope = useScope();
    const hospitalTimezone = scope?.hospital?.timezone?.id;
    const today = startOfDay(translateToTz(hospitalTimezone)(new Date()));

    const date = useMemo(() => {
      const query = queryString.parse(location.search);

      if (!query.date) return null;

      const localDate = createLocalDate(query.date as string);

      return isBefore(localDate, startOfDay(new Date())) ? localDate : null;
    }, [location.search]);

    // shouldResubscribe: true ensures subscription re-establishes after WebSocket reconnection
    const subscription = useSubscription<{ staffMonitoring: StaffMonitoring }>(
      gql`
        subscription staffMonitoring($date: Date) {
          staffMonitoring(date: $date) {
            title
            progress
            timeOfDay
            subjects {
              id
              role
              title
              entityId
              physician {
                id
                name
              }
              staffMember {
                id
                name
                compensation
                hourlyRate
              }
              anesthesiologist {
                id
                name
                hourlyRate
              }
              currentLocation {
                room {
                  id
                  name
                  type
                }
                arrivedAt
              }
              arrivedOnSite
              arrivedOnSiteOverride
              leftSite
              leftSiteOverride
            }
          }
        }
      `,
      {
        skip: Boolean(date),
        shouldResubscribe: true,
      }
    );

    const query = useQuery<{ staffMonitoring: StaffMonitoring }>(
      gql`
        query staffMonitoring($date: Date!) {
          staffMonitoring(date: $date) {
            title
            progress
            timeOfDay
            subjects {
              id
              role
              title
              entityId
              physician {
                id
                name
              }
              staffMember {
                id
                name
                compensation
                hourlyRate
              }
              anesthesiologist {
                id
                name
                hourlyRate
              }
              currentLocation {
                room {
                  id
                  name
                  type
                }
                arrivedAt
              }

              arrivedOnSite
              arrivedOnSiteOverride
              leftSite
              leftSiteOverride
            }
          }
        }
      `,
      {
        variables: {
          date: date ? formatLocalDate(date) : null,
        },
        skip: !date,
      }
    );

    const { loading, error, data } = date ? query : subscription;

    // Cache valid subscription data to prevent flash of empty state during reconnection
    useEffect(() => {
      const currentData = data?.staffMonitoring;
      if (currentData && currentData.subjects && currentData.subjects.length > 0) {
        cachedDataRef.current = currentData;
        // Clear reconnecting state once valid data arrives
        if (isReconnecting) {
          setIsReconnecting(false);
        }
      }
    }, [data?.staffMonitoring, isReconnecting]);

    // Use cached data during reconnection to prevent flash of empty state
    const staffMonitoringData = useMemo(() => {
      const currentData = data?.staffMonitoring;

      // If we have valid current data, use it
      if (currentData && currentData.subjects && currentData.subjects.length > 0) {
        return currentData;
      }

      // During reconnection, use cached data to prevent flash of empty state
      if (isReconnecting && cachedDataRef.current) {
        console.log('[StaffMonitor] Using cached data during reconnection');
        return cachedDataRef.current;
      }

      // Return current data (may be empty for first load or truly empty state)
      return currentData;
    }, [data?.staffMonitoring, isReconnecting]);

    const { title, progress, timeOfDay, subjects } = staffMonitoringData ?? {};

    const [settings, setSettings] = useSettings(columns);

    // Determine if this is a live view (today) or historical view (past date)
    const isLiveView = !date;

    if (loading && view === 'table') {
      return <LoadingIndicator />;
    }

    if (error && view === 'table') {
      return <pre>Error: {String(error)}</pre>;
    }

    // Render Analytics View (only if not tableOnly mode)
    if (view === 'analytics' && !tableOnly) {
      return (
        <StaffAnalytics
          hospitalTimezone={hospitalTimezone}
          view={view}
          onViewChange={setView}
          hospitalName={scope?.hospital?.name}
          initialSelectedDate={formatLocalDate(date ?? today)}
        />
      );
    }

    // Render Table View
    return (
      <div className={classes.pageContainer}>
        {/* Header */}
        <div className={classes.headerArea}>
          <div className={classes.titleColumn}>
            <div>
              <Typography variant="h1" className={classes.hospitalName}>
                Staffing Analytics
              </Typography>
            </div>

            {/* Date Presets / Picker (Right side of header title row in React code example, but here we have view specific controls) */}
            <div className={classes.controls}>
              <div className={classes.datePicker}>
                <Dropdown
                  content={({ close }) => {
                    const handleChange = (selectedDate: string | number | Date) => {
                      close();

                      const params = queryString.parse(location.search);
                      const selectedDateObj = new Date(selectedDate);
                      const localSelectedDate = new Date(
                        selectedDateObj.getFullYear(),
                        selectedDateObj.getMonth(),
                        selectedDateObj.getDate(),
                        12,
                        0,
                        0,
                        0
                      );

                      if (isBefore(localSelectedDate, today)) {
                        params.date = formatLocalDate(localSelectedDate);
                      } else {
                        delete params.date;
                      }

                      history.replace({
                        pathname: location.pathname,
                        search: queryString.stringify(params),
                      });
                    };

                    return () => <Calendar date={date} maxDate={today} onChange={handleChange} />;
                  }}
                >
                  <ButtonDateSwitch date={date}>
                    <Typography className={classes.date}>
                      {timeOfDay && timeOfDay !== 86400000 ? formatTimeOfDay(timeOfDay) : title}
                    </Typography>
                  </ButtonDateSwitch>
                </Dropdown>
              </div>
            </div>
          </div>

          {/* Tabs row - hidden in tableOnly mode */}
          {!tableOnly && (
            <div className={classes.tabsContainer}>
              <div className={classes.tabsNav}>
                <button
                  className={view === 'table' ? classes.activeTab : classes.inactiveTab}
                  onClick={() => setView('table')}
                >
                  Table View
                </button>
                <button className={classes.inactiveTab} onClick={() => setView('analytics')}>
                  Analytics
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <Typography variant="h6" className={classes.cardTitle}>
              Staffing Table
            </Typography>
            {isLiveView && (
              <div className={classes.liveIndicator}>
                <span className={classes.liveBadge}>Today</span>
                {isReconnecting ? (
                  <span className={classes.reconnectingText}>Reconnecting...</span>
                ) : (
                  <span className={classes.liveText}>Live</span>
                )}
              </div>
            )}
          </div>

          {subjects && subjects.length !== 0 ? (
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {settings.columns
                      .filter(({ visible, key }) => {
                        if (!isLiveView && (key === 'currentLocation' || key === 'timeAtLocation')) {
                          return false;
                        }
                        return visible;
                      })
                      .map(({ key, title }) => (
                        <TableCell key={key} className={classes.tableHeadCell}>
                          {title}
                        </TableCell>
                      ))}
                    <TableCell className={classes.settingsCell}>
                      <TableSettings columns={columns} settings={settings} onChange={setSettings} />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableSection
                    entries={subjects ?? []}
                    timeOfDay={timeOfDay!}
                    settings={settings}
                    date={formatLocalDate(date ?? today)}
                    isLiveView={isLiveView}
                    disableRowClick={effectiveDisableRowClick}
                  />
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box flex={1} alignItems="center" justifyContent="center" display="flex">
              <Typography variant="h5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                No staff members on site yet
              </Typography>
            </Box>
          )}
        </div>
      </div>
    );
  }
);

const useStyles = makeStyles(theme => ({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#04153A',
    color: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  headerArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  titleColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: theme.spacing(10),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
  hospitalName: {
    fontSize: '1.75rem',
    fontWeight: 600,
    color: '#FFFFFF',
    [theme.breakpoints.up('md')]: {
      fontSize: '2rem',
    },
  },
  subTitle: {
    fontSize: '0.875rem',
    color: 'rgba(255, 255, 255, 0.6)',
    marginTop: theme.spacing(0.5),
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tabsContainer: {
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    marginTop: theme.spacing(1),
  },
  tabsNav: {
    display: 'flex',
    gap: theme.spacing(4),
    fontSize: '1.25rem',
  },
  activeTab: {
    paddingBottom: theme.spacing(1.5),
    marginBottom: -1,
    borderBottom: '2px solid #00A3FF',
    color: '#FFFFFF',
    background: 'none',
    border: 'none',
    borderBottomWidth: 2,
    cursor: 'pointer',
    fontSize: '1.25rem',
    transition: 'color 0.2s',
    '&:focus': {
      outline: 'none',
    },
  },
  inactiveTab: {
    paddingBottom: theme.spacing(1.5),
    marginBottom: -1,
    borderBottom: '2px solid transparent',
    color: 'rgba(255, 255, 255, 0.6)',
    background: 'none',
    border: 'none',
    borderBottomWidth: 2,
    cursor: 'pointer',
    fontSize: '1.25rem',
    transition: 'color 0.2s',
    '&:hover': {
      color: '#FFFFFF',
    },
    '&:focus': {
      outline: 'none',
    },
  },
  card: {
    backgroundColor: '#071B4A',
    borderRadius: 24,
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    flex: 1,
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#FFFFFF',
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  liveBadge: {
    fontSize: '0.875rem',
    padding: '4px 10px',
    borderRadius: 9999,
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    color: '#86efac',
    border: '1px solid rgba(34, 197, 94, 0.4)',
  },
  liveText: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#4ade80',
  },
  reconnectingText: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#fbbf24', // Amber color for reconnecting state
  },
  tableContainer: {
    borderRadius: 16,
    border: '1px solid rgba(255, 255, 255, 0.05)',
    flex: 1,
    overflow: 'auto',
  },
  tableHeadCell: {
    backgroundColor: '#020B26',
    color: 'rgba(255, 255, 255, 0.6)',
    textTransform: 'uppercase',
    fontSize: '0.875rem',
    fontWeight: 500,
    padding: theme.spacing(1.5, 2),
    borderBottom: 'none',
  },
  settingsCell: {
    backgroundColor: '#020B26',
    borderBottom: 'none',
    padding: theme.spacing(1.5, 2),
    width: 48,
  },
  datePicker: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  date: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '1.25rem',
    fontWeight: 500,
  },
}));

export default StaffMonitor;
